import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  AccountAdminWriteInput,
  AccountAdminsListQuery,
  AccountAdminsRepository,
  ArchivedAdminsListQuery,
  ArchivedBranchesListQuery,
  ArchivedCompaniesListQuery,
  ArchivedDepartmentsListQuery,
  ArchivedRegionsListQuery,
  BranchesListQuery,
  BranchManagementRepository,
  BranchUpdateInput,
  BranchWriteInput,
  CompaniesListQuery,
  CompaniesRepository,
  CompanyWriteInput,
  CountriesListQuery,
  CountriesRepository,
  CurrenciesListQuery,
  CurrenciesRepository,
  DepartmentCreateResult,
  DepartmentUpdateInput,
  DepartmentUpdateResult,
  DepartmentWriteInput,
  DepartmentsListQuery,
  DepartmentsRepository,
  GovernoratesListQuery,
  RegionsListQuery,
  RegionsRepository,
  RegionUpdateInput,
  RegionWriteInput,
} from "../domain";

function queryKeyParams(
  query:
    | CompaniesListQuery
    | AccountAdminsListQuery
    | ArchivedAdminsListQuery
    | ArchivedBranchesListQuery
    | ArchivedCompaniesListQuery
    | ArchivedRegionsListQuery
    | CurrenciesListQuery
    | BranchesListQuery
    | RegionsListQuery
    | DepartmentsListQuery
    | ArchivedDepartmentsListQuery
    | CountriesListQuery
    | GovernoratesListQuery
    | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}

export function useCompaniesQuery(
  repository: CompaniesRepository,
  query?: CompaniesListQuery,
) {
  return useQuery({
    queryKey: queryKeys.companies.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useArchivedCompaniesQuery(
  repository: CompaniesRepository,
  query?: ArchivedCompaniesListQuery,
) {
  return useQuery({
    // Nested under companies.lists() so a restore's `companies.all`
    // invalidation also refetches this list (the restored row drops out).
    queryKey: [
      ...queryKeys.companies.lists(),
      "archived",
      queryKeyParams(query) ?? {},
    ],
    queryFn: () => repository.listArchived(query),
  });
}

export function useCompanySummaryQuery(repository: CompaniesRepository) {
  return useQuery({
    queryKey: queryKeys.companies.summary(),
    queryFn: () => repository.getSummary(),
  });
}

export function useCompanyQuery(repository: CompaniesRepository, id: string) {
  return useQuery({
    queryKey: queryKeys.companies.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useCreateCompanyMutation(repository: CompaniesRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: CompanyWriteInput) => repository.create(input),
    // An archived match created nothing, so only a real creation touches the list.
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({ queryKey: queryKeys.companies.all });
      }
    },
  });
}

export function useRestoreCompanyMutation(repository: CompaniesRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.companies.all }),
  });
}

export function useUpdateCompanyMutation(repository: CompaniesRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CompanyWriteInput }) =>
      repository.update(id, input),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.companies.all }),
  });
}

export function useDeleteCompanyMutation(repository: CompaniesRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.companies.all }),
  });
}

export function useAccountAdminsQuery(
  repository: AccountAdminsRepository,
  query?: AccountAdminsListQuery,
) {
  return useQuery({
    queryKey: queryKeys.accountAdmins.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useArchivedAccountAdminsQuery(
  repository: AccountAdminsRepository,
  query?: ArchivedAdminsListQuery,
) {
  return useQuery({
    // Nested under accountAdmins.lists() so a restore's `accountAdmins.all`
    // invalidation also refetches this list (the restored row drops out).
    queryKey: [
      ...queryKeys.accountAdmins.lists(),
      "archived",
      queryKeyParams(query) ?? {},
    ],
    queryFn: () => repository.listArchived(query),
  });
}

export function useAccountAdminQuery(
  repository: AccountAdminsRepository,
  id: string,
) {
  return useQuery({
    queryKey: queryKeys.accountAdmins.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useCreateAccountAdminMutation(
  repository: AccountAdminsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: AccountAdminWriteInput) => repository.create(input),
    // An archived match created nothing, so only a real creation touches the list.
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({
          queryKey: queryKeys.accountAdmins.all,
        });
      }
    },
  });
}

export function useRestoreAccountAdminMutation(
  repository: AccountAdminsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.accountAdmins.all }),
  });
}

export function useUpdateAccountAdminMutation(
  repository: AccountAdminsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: AccountAdminWriteInput;
    }) => repository.update(id, input),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.accountAdmins.all }),
  });
}

export function useDeleteAccountAdminMutation(
  repository: AccountAdminsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.accountAdmins.all }),
  });
}

export function useResendAccountAdminActivationMutation(
  repository: AccountAdminsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => repository.resendActivation(email),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.accountAdmins.all }),
  });
}

export function useCurrenciesQuery(
  repository: CurrenciesRepository,
  query?: CurrenciesListQuery,
) {
  return useQuery({
    queryKey: queryKeys.currencies.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useCountriesQuery(
  repository: CountriesRepository,
  query?: CountriesListQuery,
) {
  return useQuery({
    queryKey: queryKeys.countries.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useGovernoratesQuery(
  repository: CountriesRepository,
  countryId: string | undefined,
  query?: GovernoratesListQuery,
) {
  return useQuery({
    // Not routed through queryKeyParams (typed to the plain list-query
    // union) since countryId isn't part of GovernoratesListQuery itself --
    // it's a separate path param that still needs to be in the cache key.
    queryKey: queryKeys.governorates.list({
      countryId: countryId ?? "",
      ...query,
    }),
    queryFn: () => repository.listGovernorates(countryId!, query),
    enabled: countryId !== undefined && countryId.length > 0,
  });
}

export function useRegionsQuery(
  repository: RegionsRepository,
  query?: RegionsListQuery,
) {
  return useQuery({
    queryKey: queryKeys.regions.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useRegionQuery(repository: RegionsRepository, id: string) {
  return useQuery({
    queryKey: queryKeys.regions.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useCreateRegionMutation(repository: RegionsRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: RegionWriteInput) => repository.create(input),
    // An archived match created nothing, so only a real creation touches the list.
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({ queryKey: queryKeys.regions.all });
      }
    },
  });
}

export function useUpdateRegionMutation(repository: RegionsRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RegionUpdateInput }) =>
      repository.update(id, input),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.regions.all }),
  });
}

export function useArchiveRegionMutation(repository: RegionsRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.archive(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.regions.all }),
  });
}

export function useArchivedRegionsQuery(
  repository: RegionsRepository,
  query?: ArchivedRegionsListQuery,
) {
  return useQuery({
    queryKey: [...queryKeys.regions.lists(), "archived", queryKeyParams(query) ?? {}],
    queryFn: () => repository.listArchived(query),
  });
}

export function useRestoreRegionMutation(repository: RegionsRepository) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.regions.all }),
  });
}

export function useBranchManagementQuery(
  repository: BranchManagementRepository,
  query?: BranchesListQuery,
) {
  return useQuery({
    queryKey: queryKeys.branchManagement.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useBranchManagementDetailQuery(
  repository: BranchManagementRepository,
  id: string,
) {
  return useQuery({
    queryKey: queryKeys.branchManagement.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useCreateBranchMutation(
  repository: BranchManagementRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: BranchWriteInput) => repository.create(input),
    // An archived match created nothing, so only a real creation touches the list.
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({
          queryKey: queryKeys.branchManagement.all,
        });
      }
    },
  });
}

export function useUpdateBranchMutation(
  repository: BranchManagementRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: BranchUpdateInput }) =>
      repository.update(id, input),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.branchManagement.all }),
  });
}

export function useArchiveBranchMutation(
  repository: BranchManagementRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.archive(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.branchManagement.all }),
  });
}

export function useArchivedBranchesQuery(
  repository: BranchManagementRepository,
  query?: ArchivedBranchesListQuery,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.branchManagement.lists(),
      "archived",
      queryKeyParams(query) ?? {},
    ],
    queryFn: () => repository.listArchived(query),
  });
}

export function useRestoreBranchMutation(
  repository: BranchManagementRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.branchManagement.all }),
  });
}

export function useDepartmentsQuery(
  repository: DepartmentsRepository,
  query?: DepartmentsListQuery,
) {
  return useQuery({
    queryKey: queryKeys.departments.list(queryKeyParams(query)),
    queryFn: () => repository.list(query),
  });
}

export function useArchivedDepartmentsQuery(
  repository: DepartmentsRepository,
  query?: ArchivedDepartmentsListQuery,
) {
  return useQuery({
    // Nested under departments.lists() so a restore's `departments.all`
    // invalidation refetches both lists (the row moves between them).
    queryKey: [
      ...queryKeys.departments.lists(),
      "archived",
      queryKeyParams(query) ?? {},
    ],
    queryFn: () => repository.listArchived(query),
  });
}

export function useRestoreDepartmentMutation(
  repository: DepartmentsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.restore(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: queryKeys.departments.all }),
  });
}

/** A thrown write, carrying the backend's localized message when it sent one
 *  (400/403/404/500) -- the screen falls back to its own copy otherwise. */
type DepartmentWriteFailure = {
  readonly status: "failed";
  readonly message?: string;
};

function departmentWriteFailure(error: unknown): DepartmentWriteFailure {
  console.error(error);
  return error instanceof Error && error.message.length > 0
    ? { status: "failed", message: error.message }
    : { status: "failed" };
}

export type CreateDepartmentOutcome =
  DepartmentCreateResult | DepartmentWriteFailure;

export function useCreateDepartmentMutation(repository: DepartmentsRepository) {
  const client = useQueryClient();

  return useMutation({
    // A failure resolves to `failed` rather than throwing, so the screen
    // owns the one toast (a thrown error would also raise the global one).
    mutationFn: async (
      input: DepartmentWriteInput,
    ): Promise<CreateDepartmentOutcome> => {
      try {
        return await repository.create(input);
      } catch (error) {
        return departmentWriteFailure(error);
      }
    },
    onSuccess: async (result) => {
      if (result.status === "created") {
        await client.invalidateQueries({ queryKey: queryKeys.departments.all });
      }
    },
  });
}

export function useDepartmentQuery(
  repository: DepartmentsRepository,
  id: string,
) {
  return useQuery({
    queryKey: queryKeys.departments.detail(id),
    queryFn: () => repository.getById(id),
    enabled: id.length > 0,
  });
}

export function useArchiveDepartmentMutation(
  repository: DepartmentsRepository,
) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => repository.archive(id),
    // Both lists change: the row leaves the active list and joins the archive.
    // Also on failure -- a 404 means someone else already deleted it.
    onSettled: async () =>
      client.invalidateQueries({ queryKey: queryKeys.departments.all }),
  });
}

export type UpdateDepartmentOutcome =
  DepartmentUpdateResult | DepartmentWriteFailure;

export function useUpdateDepartmentMutation(repository: DepartmentsRepository) {
  const client = useQueryClient();

  return useMutation({
    // Same as create: a failure resolves to `failed` with the backend's text.
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: DepartmentUpdateInput;
    }): Promise<UpdateDepartmentOutcome> => {
      try {
        return await repository.update(id, input);
      } catch (error) {
        return departmentWriteFailure(error);
      }
    },
    onSuccess: async (result) => {
      if (result.status === "updated") {
        await client.invalidateQueries({ queryKey: queryKeys.departments.all });
      }
    },
  });
}
